import React, { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import Swal from "sweetalert2";
import axios from "axios";

const base_url = process.env.REACT_APP_API_URL;

export default function AdminAviator() {
  const socketRef = useRef(null);
  const token = localStorage.getItem("admin_token");

  const [flightVal, setFlightVal] = useState(1.0);
  const [roundId, setRoundId] = useState("");
  const [users, setUsers] = useState(0);
  const [betAmount, setBetAmount] = useState(0);
  const [profitAmount, setProfitAmount] = useState(0);
  const [profitPercentage, setProfitPercentage] = useState(0);
  const [recentHistory, setRecentHistory] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [inputProfitAmount, setInputProfitAmount] = useState("");
  const [inputProfitPercentage, setInputProfitPercentage] = useState("");

  const [nextCrash, setNextCrash] = useState("");

  const [liveBets, setLiveBets] = useState([]);
  const [historyBets, setHistoryBets] = useState([]);
  const [view, setView] = useState("current");

  /* =========================
  GET GAME DATA
  ========================= */

  const getGameData = async () => {
    const res = await axios.get(base_url + "/admin/manage/aviatorHistoryData", {
      headers: {
        Authorization: token,
      },
    });

    if (!res.data.status) return;

    setLiveBets(res.data.data.liveData);
    setHistoryBets(res.data.data.historyData);
  };

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL, {
      transports: ["websocket"],
      reconnection: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("🟢 Admin Aviator socket connected:", socket.id);
    });

    socket.on("connect_error", (err) => {
      console.error("🔴 Admin Aviator socket error:", err.message);
    });

    socket.on("admin:crash-point-set", (data) => {
      console.log("🎯 Crash point confirmed:", data);

      setNextCrash("");

      Swal.fire({
        icon: "success",
        title: "Crash Point Set",
        text: `Next crash: ${Number(data.crashPoint).toFixed(2)}x`,
        timer: 1500,
        showConfirmButton: false,
      });
    });

    socket.on("admin:crash-now-result", (data) => {
      console.log("🚨 Crash response:", data);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  /* =========================
  SOCKET EVENTS
  ========================= */
  useEffect(() => {
    const socket = socketRef.current;

    if (!socket) return;

    const handleSyncState = (state) => {
      console.log("SYNC:", state);

      setFlightVal(Number(state.multiplier || 1));
      setRoundId(state.gameid || "");
      setUsers(Number(state.betCount || 0));
      setBetAmount(Number(state.totalBetAmount || 0));
      setProfitAmount(Number(state.totalProfitAmount || 0));
      setProfitPercentage(Number(state.profitPercentage || 0));
    };

    const handleMultiplier = (value) => {
      setFlightVal(Number(value));
    };

    const handleCrashHistory = (history) => {
      const list = history.map((h) =>
        Number(h.result).toFixed(2)
      );

      setRecentHistory(list);
    };

    // 🔥 NEW BET
    const handleAdminBet = async (data) => {
      console.log("🔥 NEW BET:", data);

      if (data?.gameid) {
        setRoundId(data.gameid);
      }

      if (data?.betCount !== undefined) {
        setUsers(Number(data.betCount));
      }

      if (data?.totalBetAmount !== undefined) {
        setBetAmount(Number(data.totalBetAmount));
      }

      if (data?.totalProfitAmount !== undefined) {
        setProfitAmount(Number(data.totalProfitAmount));
      }

      if (data?.profitPercentage !== undefined) {
        setProfitPercentage(Number(data.profitPercentage));
      }

      // 🔥 Get actual DB data
      await getGameData();
    };

    const handleCashoutAdmin = (data) => {
      console.log("💰 CASHOUT PROFIT UPDATE:", data);

      if (data?.totalProfitAmount !== undefined) {
        setProfitAmount(Number(data.totalProfitAmount));
      }

      if (data?.profitPercentage !== undefined) {
        setProfitPercentage(Number(data.profitPercentage));
      }

      // Refresh table only
      getGameData();
    };

    socket.on("sync_state", handleSyncState);
    socket.on("multiplier_update", handleMultiplier);
    socket.on("crash_history", handleCrashHistory);

    socket.on("round_start", handleRoundStart);

    // 🔥 IMPORTANT
    socket.on("bet_success_for_admin", handleAdminBet);

    socket.on("cashout_success_for_admin", handleCashoutAdmin);

    return () => {
      socket.off("sync_state", handleSyncState);
      socket.off("multiplier_update", handleMultiplier);
      socket.off("crash_history", handleCrashHistory);
      socket.off("bet_success_for_admin", handleAdminBet);
      socket.off("cashout_success_for_admin", handleCashoutAdmin);
    };
  }, []);


  const handleRoundStart = async (data) => {
    console.log("🚀 NEW ROUND START:", data);

    // Update current round immediately
    setRoundId(data?.gameid || "");

    // Reset/update live counters
    setUsers(Number(data?.betCount || 0));
    setBetAmount(Number(data?.totalBetAmount || 0));

    // Reset multiplier
    setFlightVal(1);

    // 🔥 Reload LIVE + HISTORY from database
    await getGameData();
  };

  /* =========================
  SET RESULT
  ========================= */

  const handleSetResult = () => {
    const val = Number(nextCrash);

    if (!Number.isFinite(val) || val <= 1) {
      return Swal.fire(
        "Invalid",
        "Enter value greater than 1.0",
        "error"
      );
    }

    const socket = socketRef.current;

    if (!socket || !socket.connected) {
      return Swal.fire(
        "Socket Error",
        "Admin server connection is not ready.",
        "error"
      );
    }

    console.log("🎯 Sending crash point:", val);

    socket.emit(
      "admin:set-crash-point",
      val,
      (response) => {
        console.log("🎯 Server response:", response);

        if (!response?.status) {
          Swal.fire(
            "Error",
            response?.message || "Could not set crash point",
            "error"
          );
          return;
        }

        setNextCrash("");

        Swal.fire({
          icon: "success",
          title: "Crash Point Locked",
          text: `Crash fixed at ${val.toFixed(2)}x`,
          timer: 1500,
          showConfirmButton: false,
        });
      }
    );
  };

  /* =========================
  FORCE CRASH
  ========================= */

  const crashNow = () => {
    const socket = socketRef.current;

    if (!socket || !socket.connected) {
      return Swal.fire(
        "Socket Error",
        "Admin server connection is not ready.",
        "error"
      );
    }

    console.log("🚨 Sending FORCE CRASH");

    socket.emit("admin:crash-now", (response) => {
      console.log("🚨 Crash response:", response);

      if (response?.status === false) {
        Swal.fire(
          "Cannot Crash",
          response.message || "Unable to crash",
          "error"
        );
        return;
      }

      Swal.fire({
        icon: "warning",
        title: "Crash Triggered",
        text: "Current round will crash.",
        timer: 1000,
        showConfirmButton: false,
      });
    });
  };



  useEffect(() => {
    getGameData();
  }, []);

  const handleUpdateProfit = async () => {
    // if (!inputProfitAmount || !inputProfitPercentage) {
    //   return Swal.fire("Error", "All fields required", "error");
    // }

    try {
      const res = await axios.post(
        base_url + "/admin/manage/profit_percentage/aviator",
        {
          profit_amount: inputProfitAmount,
          profit_percentage: inputProfitPercentage,
        },
        {
          headers: { Authorization: token },
        },
      );

      if (res.data.status) {
        Swal.fire("Success", "Updated successfully", "success");

        setProfitAmount(inputProfitAmount);
        setProfitPercentage(inputProfitPercentage);

        setShowModal(false);
      } else {
        Swal.fire("Error", res.data.message, "error");
      }
    } catch (err) {
      Swal.fire("Error", "Server error", "error");
    }
  };

  const tableData = view === "current" ? liveBets : historyBets;

  return (
    <>
      <div className="container-fluid">
        {/* TOP STATS */}

        <div className="row mb-4">
          <div className="col-md-2">
            <div className="card p-3">
              <span>Round ID</span>
              <h3>{roundId}</h3>
            </div>
          </div>

          <div className="col-md-2">
            <div className="card p-3">
              <span>Live Players</span>
              <h3>{users}</h3>
            </div>
          </div>

          <div className="col-md-2">
            <div className="card p-3">
              <span>Bet Amount</span>
              <h3>{betAmount}</h3>
            </div>
          </div>

          <div className="col-md-2">
            <div className="card p-3">
              <span>Profit %</span>
              <h3>{profitPercentage}</h3>
            </div>
          </div>

          <div className="col-md-2">
            <div className="card p-3">
              <span>Profit Amount</span>
              <h3>{profitAmount}</h3>
            </div>
          </div>

          <div className="col-md-2">
            <button
              className="btn btn-danger w-100 mt-4"
              onClick={() => {
                setInputProfitAmount(profitAmount);
                setInputProfitPercentage(profitPercentage);
                setShowModal(true);
              }}
            >
              Update Profit
            </button>
          </div>
        </div>

        {/* RECENT MULTIPLIERS */}

        <div className="mb-3 d-flex gap-2 overflow-auto">
          {recentHistory.map((val, i) => {
            const cls =
              val >= 10 ? "bg-primary" : val >= 2 ? "bg-danger" : "bg-secondary";

            return (
              <div key={i} className={`badge ${cls}`}>
                {val}x
              </div>
            );
          })}
        </div>

        {/* CHART + CONTROLS */}

        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card p-3">
              <h6>Game Control</h6>

              <input
                type="number"
                className="form-control mb-3"
                placeholder="1.50"
                value={nextCrash}
                onChange={(e) => setNextCrash(e.target.value)}
              />

              <button
                className="btn btn-primary w-100 mb-2"
                onClick={handleSetResult}
              >
                Set Result
              </button>

              <button className="btn btn-danger w-100" onClick={crashNow}>
                Force Crash
              </button>
            </div>
          </div>

          <div className="col-md-6 text-center">
            <div className="card p-3">
              <small>CURRENT MULTIPLIER</small>

              <h1 className="text-primary">{flightVal.toFixed(2)}x</h1>
            </div>
          </div>
        </div>

        {/* TABLE */}

        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h6>Bets Monitor</h6>

            <div>
              <button
                className={`btn btn-sm ${view === "current" ? "btn-primary" : "btn-dark"}`}
                onClick={() => setView("current")}
              >
                LIVE
              </button>

              <button
                className={`btn btn-sm ${view === "history" ? "btn-primary" : "btn-dark"}`}
                onClick={() => setView("history")}
              >
                HISTORY
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table text-center">
              <thead>
                <tr>
                  <th>ROUND</th>
                  <th>USER</th>
                  <th>AMOUNT</th>
                  <th>P/L</th>
                  <th>STATUS</th>
                </tr>
              </thead>

              <tbody>
                {tableData.map((b, i) => {
                  const isWin = Number(b.status) === 1;
                  const isRunning = Number(b.status) === 2;

                  const plAmount = isWin
                    ? `+₹${Number(b.winAmount).toFixed(2)}`
                    : `-₹${Number(b.amount).toFixed(2)}`;

                  return (
                    <tr key={i}>
                      <td>{b.gameid}</td>
                      <td>{b.userid}</td>
                      <td>₹{Number(b.amount).toFixed(2)}</td>

                      <td className={isWin ? "text-success" : "text-danger"}>
                        {isRunning ? "-" : plAmount}
                      </td>

                      <td>{isRunning ? "RUNNING" : isWin ? "WON" : "LOST"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ✅ MODAL MUST BE HERE */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Update Profit & Percentage</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <input
                  type="number"
                  className="form-control mb-3"
                  placeholder="Profit Amount"
                  value={inputProfitAmount}
                  onChange={(e) => setInputProfitAmount(e.target.value)}
                />

                <input
                  type="number"
                  className="form-control"
                  placeholder="Profit %"
                  value={inputProfitPercentage}
                  onChange={(e) => setInputProfitPercentage(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>

                <button className="btn btn-primary" onClick={handleUpdateProfit}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
